import { _isNotBlank } from '@/littledash';
import { type DeviceType } from '@/utils/devices/DeviceEventBus';
import { type FC, type ReactNode } from 'react';
import { toast, type ToastOptions } from 'react-toastify';
import DeviceTypeIcon from './DeviceTypeIcon';
import styles from './DataTableDeviceToast.module.scss';

type DeviceToastSeverity = 'success' | 'warning' | 'error';

const SEVERITY_ICON_CLASS: Record<DeviceToastSeverity, string> = {
  success: 'light-green',
  warning: 'light-yellow',
  error: 'light-red',
};

const SEVERITY_AUTO_CLOSE: Record<DeviceToastSeverity, number> = {
  success: 2000,
  warning: 3500,
  error: 5000,
};

interface DeviceToastContentProps {
  deviceName: string;
  detail: ReactNode;
  severity: DeviceToastSeverity;
  deviceType: DeviceType | undefined;
}

const FALLBACK_DEVICE_NAME = 'Device';

/**
 * Body for a device-reading toast: an icon inferred from the device type
 * (falling back to a generic glyph), tinted by severity, heading a two-line body
 * with the device name on the first line and the reading (or status message) on
 * the second.
 */
const DeviceToastContent: FC<DeviceToastContentProps> = ({ deviceName, detail, severity, deviceType }) => (
  <div>
    <div className={styles.header}>
      <DeviceTypeIcon deviceType={deviceType} className={SEVERITY_ICON_CLASS[severity]} />
      <span className={`fw6 white lh-title ${styles.deviceName}`}>
        {_isNotBlank(deviceName) ? deviceName : FALLBACK_DEVICE_NAME}
      </span>
    </div>
    <div className={`f6 lh-copy light-gray ${styles.detail}`}>{detail}</div>
  </div>
);

/**
 * Renders a compact, bottom-right device-reading toast. Each call produces an
 * independent toast (no shared id) so rapid readings surface individually and
 * auto-close on a severity-specific timer. The icon is rendered inside the
 * content rather than in react-toastify's fixed icon column.
 */
const showDeviceToast = ({ deviceName, detail, severity, deviceType }: DeviceToastContentProps): void => {
  const options: ToastOptions = {
    type: severity,
    icon: false,
    autoClose: SEVERITY_AUTO_CLOSE[severity],
    position: 'bottom-right',
    className: styles.deviceToast,
    closeButton: false,
    closeOnClick: false,
  };
  toast(
    <DeviceToastContent deviceName={deviceName} detail={detail} severity={severity} deviceType={deviceType} />,
    options
  );
};

/** Confirms a device reading was written to the selected cell. */
export const deviceWriteSuccessToast = (
  deviceTitle: string,
  valueLine: string,
  deviceType: DeviceType | undefined
): void => {
  showDeviceToast({ deviceName: deviceTitle, detail: valueLine, severity: 'success', deviceType });
};

/** Warns that the selected column cannot accept a numeric device reading. */
export const deviceWriteInvalidColumnToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Can only write to numeric columns',
    severity: 'warning',
    deviceType,
  });
};

/** Warns that the selected column is read-only or locked. */
export const deviceWriteLockedColumnToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Column is read-only or locked',
    severity: 'warning',
    deviceType,
  });
};

/** Warns that there is no selected cell to receive a device reading. */
export const deviceNoCellSelectedToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'No cell selected for device reading',
    severity: 'warning',
    deviceType,
  });
};

/** Warns that the selected column is not part of the active workflow. */
export const deviceColumnNotInWorkflowToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Column is not part of the active workflow',
    severity: 'warning',
    deviceType,
  });
};

/** Reports that writing a device reading to the cell failed. */
export const deviceWriteFailedToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Failed to update cell. Please try again.',
    severity: 'error',
    deviceType,
  });
};

/** Confirms a scanned value resolved to a single animal and moved the selection. */
export const deviceScanMatchToast = (
  deviceTitle: string,
  scannedValue: string,
  deviceType: DeviceType | undefined
): void => {
  showDeviceToast({ deviceName: deviceTitle, detail: scannedValue, severity: 'success', deviceType });
};

/** Warns that a scanned value matched no animal in the table. */
export const deviceScanNoMatchToast = (
  deviceTitle: string,
  scannedValue: string,
  deviceType: DeviceType | undefined
): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: `No animal found matching "${scannedValue}"`,
    severity: 'warning',
    deviceType,
  });
};

/** Reports that a scanned value matched more than one animal. */
export const deviceScanMultipleMatchesToast = (
  deviceTitle: string,
  scannedValue: string,
  deviceType: DeviceType | undefined
): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: `Multiple animals match "${scannedValue}"`,
    severity: 'error',
    deviceType,
  });
};

/** Warns that the table data is not yet loaded, so a scan cannot be resolved. */
export const deviceScanTableNotReadyToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Table data is not yet available. Please wait for the table to load.',
    severity: 'warning',
    deviceType,
  });
};

/** Reports that searching for an animal from a scanned value failed. */
export const deviceScanFailedToast = (deviceTitle: string, deviceType: DeviceType | undefined): void => {
  showDeviceToast({
    deviceName: deviceTitle,
    detail: 'Failed to search for animal. Please try again.',
    severity: 'error',
    deviceType,
  });
};
